/**
 * Dates the 7R graph cards show (OE-265). Kept apart from the graphs so the dashboard grid and
 * the Statistics page label a card from that card's own dates. Plain Date (no moment) so it is
 * testable on its own.
 */

/**
 * The working day a daily 7R graph covers: the chosen calendar day, from the time the company
 * month starts its working day to the time it ends it. Only the time of day comes from the
 * month boundaries — grafting just the day number onto the month's END labelled 27 Oct "Nov 27".
 *
 * With `timeZone` (the company's), the hours are read and set on that clock: a viewer far ahead
 * of it (Australia, for an SA company) sees 17:00 SAST as 01:00 the next day, and local hours put
 * the closing before the opening. Without it, the viewer's own clock (whole-day windows).
 */
export function dailyWorkingWindow(
    dateForData: Date,
    monthStart: Date,
    monthEnd: Date,
    timeZone?: string
): { from: Date; to: Date } {
    const at = (boundary: Date) => {
        // `wall`'s local fields read the boundary on the chosen clock; `offset` is that clock's lead on UTC.
        const wall = wallClock(boundary, timeZone);
        const fields = [wall.getHours(), wall.getMinutes(), wall.getSeconds(), boundary.getMilliseconds()] as const;
        const offset = Date.UTC(wall.getFullYear(), wall.getMonth(), wall.getDate(), ...fields) - boundary.getTime();
        const day = Date.UTC(dateForData.getFullYear(), dateForData.getMonth(), dateForData.getDate(), ...fields);
        return new Date(day - offset);
    };
    return { from: at(monthStart), to: at(monthEnd) };
}

/**
 * The same instant as a Date whose LOCAL fields read the wall clock of `timeZone`. Every time on
 * the graphs is formatted from local fields (moment, d3.timeFormat, the date pipe), so formatting
 * this shows the time in that zone. Only for display: never compute with the result. No zone =
 * the viewer's own clock, so the date comes back untouched.
 */
export function wallClock(date: Date, timeZone: string | undefined): Date {
    if (!timeZone || !(date instanceof Date) || isNaN(date.getTime())) return date;
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
    }).formatToParts(date);
    const n = (type: string) => Number(parts.find((p) => p.type === type)?.value || 0);
    return new Date(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second'), date.getMilliseconds());
}

/**
 * The instant `timeZone`'s clock reads the given wall time (month 0-based, as Date). For counting
 * on the company's clock: the device's own clock moved Flash's 08:00-17:00 to 10:00-19:00 SAST on a
 * UK computer. No zone = the device's clock.
 */
export function zonedTime(
    year: number,
    month: number,
    day: number,
    hour: number,
    minute = 0,
    second = 0,
    ms = 0,
    timeZone?: string
): Date {
    if (!timeZone) return new Date(year, month, day, hour, minute, second, ms);
    const wanted = Date.UTC(year, month, day, hour, minute, second, ms);
    // The zone's lead on UTC at instant t; asked twice so a summer-time change between the guess
    // and the answer still lands on the right offset.
    const lead = (t: number) => {
        const w = wallClock(new Date(t), timeZone);
        return (
            Date.UTC(w.getFullYear(), w.getMonth(), w.getDate(), w.getHours(), w.getMinutes(), w.getSeconds(), ms) - t
        );
    };
    const first = wanted - lead(wanted);
    return new Date(wanted - lead(first));
}

/** `date`'s calendar day on `timeZone`'s clock, at the given time on that clock. */
export function zonedDayAt(
    date: Date,
    timeZone: string | undefined,
    hour: number,
    minute = 0,
    second = 0,
    ms = 0
): Date {
    const day = wallClock(date, timeZone);
    return zonedTime(day.getFullYear(), day.getMonth(), day.getDate(), hour, minute, second, ms, timeZone);
}

const PHONE = 320;
const TABLET_SM = 534;

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const two = (n: number) => String(n).padStart(2, '0');
const hm = (d: Date) => `${d.getHours()}:${two(d.getMinutes())}`;
const dayShort = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
const dayLong = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
const dayNumeric = (d: Date) => `${two(d.getDate())}/${two(d.getMonth() + 1)}`;

/** A card's subtitle at a given card width: shorter text for narrower cards. */
export function graphSubtitle(type: string, from: Date | undefined, to: Date | undefined, width: number): string {
    if (!from || !to) return '';
    if (type === 'daily7r') {
        const hours = `${hm(from)} - ${hm(to)}`;
        if (width <= PHONE) return hours;
        if (width <= TABLET_SM) return `${hours}, ${dayShort(from)}`;
        return `${WEEKDAYS[from.getDay()]} ${hours}, ${dayShort(from)}`;
    }
    if (type === 'monthly7r') {
        if (width <= PHONE) return `${dayNumeric(from)} - ${dayNumeric(to)}`;
        if (width <= TABLET_SM) return `${dayShort(from)} - ${dayShort(to)}`;
        return `${dayLong(from)} - ${dayLong(to)}`;
    }
    return '';
}

/**
 * The viewer's clock zone, named the way people say it ("SAST"). Every time on a 7R graph is in
 * the viewer's own zone, so the graph titles carry this (OE-265: "06:00 - 15:00" said nothing about
 * whose 06:00).
 */
export function zoneLabel(date: Date = new Date(), timeZone?: string): string {
    const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Africa/Johannesburg') return 'SAST';
    const part = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(date)
        .find((p) => p.type === 'timeZoneName');
    const name = part?.value || '';
    return name === 'GMT+0' ? 'GMT' : name;
}

/** The narrowest gap the month axis's rotated day labels need. */
const MIN_TICK_SPACING = 56;

/**
 * Every n-th date, so axis labels fit the plot width (OE-265: the month axis labelled every
 * working day whatever the width, piling ~22 rotated labels on a dashboard card).
 */
export function thinTicks(dates: Date[], plotWidth: number): Date[] {
    if (dates.length === 0) return [];
    const room = Math.max(1, Math.floor(plotWidth / MIN_TICK_SPACING));
    const step = Math.max(1, Math.ceil(dates.length / room));
    return dates.filter((_, i) => i % step === 0);
}
